
import { computed, defineComponent, reactive, ref, watch, WatchStopHandle } from 'vue';
import { Coupon as Coupon_, CouponProviderInstantwinSettings } from '@comsbi/common/src/types/models';
import Coupon from '@/components/organisms/coupon/Coupon.vue';
import CustomerCoupon from '@/components/organisms/coupon/CustomerCoupon.vue';
import { useCoupon } from '@/compositions/useCoupon';

type Props = {
  providerId: string;
  token: string;
  result: {
    instantwin: CouponProviderInstantwinSettings & {
      coupons: Coupon_[];
    };
    line: {
      link: string;
      displayName: string;
      pictureUrl?: string;
    };
  };
};

export default defineComponent<Props>({
  components: { CustomerCoupon, Coupon },
  props: {
    providerId: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    result: {
      type: Object,
      required: false,
    },
  },
  setup(props: Props) {
    const { getInstantwinResult, drawInstantwin, triggerWithInstantwinResult } = useCoupon();

    const instantwin = computed(() => props.result?.instantwin || undefined);
    const coupons = computed(() => props.result?.instantwin?.coupons || []);
    const line = computed(() => props.result?.line || { link: '', displayName: '', pictureUrl: '' });

    const state = reactive<{
      screen: string;
      resultText?: string;
      active: boolean;
      progress: boolean;
      customerCoupons: any[] | null;
      // CS-7026
      showAnimation: boolean;
      // CS-8156
      drawId: string | null;
      errors: any[];
    }>({
      screen: '抽選する',
      active: false,
      progress: false,
      customerCoupons: null,
      // CS-7026
      showAnimation: false,
      // CS-8156
      drawId: null,
      errors: [],
    });

    const playAnimation = async (result: string /** 'あたり' | 'はずれ'*/) => {
      let unwatch: WatchStopHandle | null = null;
      try {
        state.showAnimation = true;
        await new Promise(resolve => setTimeout(resolve, 0));
        await new Promise((resolve, reject) => {
          const videoElm = document.getElementById('animation') as HTMLVideoElement;
          videoElm.style.opacity = '0';
          videoElm.oncanplay = () => {
            videoElm.style.opacity = '1.0';
          };
          videoElm.onended = resolve;
          videoElm.onerror = reject;
          videoElm.src =
            process.env.VUE_APP_RETAIL_URL +
            `/img/instantwin/optimize/${result === 'あたり' ? 'success' : 'failed'}.mp4`;
          unwatch = watch(
            () => state.showAnimation,
            value => {
              if (value) return;
              if (unwatch) reject('closeModal');
            }
          );
        });
      } catch (e) {
        console.warn(e);
      } finally {
        if (unwatch) {
          (unwatch as WatchStopHandle)();
          unwatch = null;
        }
        state.showAnimation = false;
      }
    };

    const draw = async () => {
      //
      try {
        state.progress = true;
        const [result, _]: any[] = await Promise.all([
          drawInstantwin(props.providerId, props.token),
          new Promise(resolve => setTimeout(resolve, 3000)),
        ]);

        // CS-9330 当選したがエラーでクーポンが発行されなかった場合
        if (result.won && result.isError) {
          // 当選エラーとして扱う
          await playAnimation('あたり'); // 当選アニメーションは表示
          state.screen = '当選エラー';
          state.drawId = result.drawId;
          state.errors = result.errors;
          return;
        }

        const screen = result.won ? 'あたり' : 'はずれ';
        // CS-7026 抽選アニメーション
        await playAnimation(screen);
        state.screen = screen;
        state.resultText = result.text;
        state.customerCoupons = result.customerCoupons;
        state.drawId = result.drawId;

        // CS-6905 抽選結果ポストバック => CS-9014 バックエンドへ移行
      } catch (e) {
        //
        state.screen = 'エラー';
        console.error(e);
      }
    };

    // useAsync
    (async () => {
      // 抽選済結果取得
      try {
        const result: any = await getInstantwinResult(props.providerId, props.token, state.drawId ?? undefined);
        switch (true) {
          case result.drawn && result.won:
            state.screen = 'あたり済み';
            break;
          case result.drawn && !result.won:
            state.screen = 'はずれ済み';
            break;
          default:
            state.screen = '抽選する';
            state.active = true;
        }
      } catch (e) {
        state.screen = 'エラー';
      }
    })();

    return {
      instantwin,
      coupons,
      line,
      state,
      resultText: computed(() => {
        return state.resultText?.replace(/[\r\n|\r]/g, '<br/>');
      }),
      winText: computed(() => {
        return instantwin.value ? instantwin.value.winText?.replace(/[\r\n|\r]/g, '<br/>') : '';
      }),
      loseText: computed(() => {
        return instantwin.value ? instantwin.value.loseText?.replace(/[\r\n|\r]/g, '<br/>') : '';
      }),

      draw,
    };
  },
});
